import { LitElement, html, nothing } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhInfo";
import "@phosphor-icons/webcomponents/PhPause";
import "@phosphor-icons/webcomponents/PhPlay";
import "@phosphor-icons/webcomponents/PhStop";
import "@phosphor-icons/webcomponents/PhWarning";
import "@phosphor-icons/webcomponents/PhX";
import "ui-components/button/wow-button";
import "ui-components/modal/wow-modal";
import "ui-components/stream-recorder/stream-recorder";
import "ui-components/live-badge/live-badge";
import "ui-components/go-live-button/go-live-button";
import "ui-components/confirmation/wow-confirmation";
import "ui-components/live-chat/live-chat";

import { t, translate } from "../../translations/i18n";
import { rpc } from "ui-common/services/rpc";
import { NotificationService } from "ui-components/notification";

import type { StreamRecorder } from "ui-components/stream-recorder/stream-recorder";
import type { LiveBadgeStatus } from "ui-components/live-badge/live-badge";

import { styles } from "./live-stream-modal.css";

@customElement("live-stream-modal")
export class LiveStreamModal extends LitElement {
  static override styles = [styles];

  @property({ type: String })
  streamId!: string;

  @property({ type: Boolean, attribute: "enable-audio" })
  enableAudio = true;

  @property({ type: Boolean, attribute: "is-live" })
  isLive = false;

  @query("stream-recorder")
  private streamRecorder?: StreamRecorder;

  @state()
  private isStreaming = false;

  @state()
  private isPaused = false;

  @state()
  private isConnecting = false;

  @state()
  private streamingDuration = 0;

  @state()
  private errorMessage = "";

  @state()
  private showEndConfirmation = false;

  @state()
  private isEnding = false;

  private durationInterval: number | null = null;

  override connectedCallback() {
    super.connectedCallback();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.stopDurationTimer();
  }

  override firstUpdated() {
    void this.startStream();
  }

  private async startStream(): Promise<void> {
    if (!this.streamRecorder) return;

    this.isConnecting = true;
    this.errorMessage = "";

    try {
      await this.streamRecorder.startStreaming();
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : "Failed to start streaming";
      this.isConnecting = false;
    }
  }

  private handleStreamingStarted(): void {
    this.isStreaming = true;
    this.isConnecting = false;
    this.startDurationTimer();
  }

  private handleStreamingStopped(): void {
    this.isStreaming = false;
    this.isPaused = false;
    this.stopDurationTimer();
    this.dispatchEvent(new CustomEvent("stream-ended", { bubbles: true, composed: true }));
  }

  private handleStreamingError(e: CustomEvent): void {
    this.errorMessage = e.detail?.message || "Streaming error occurred";
    this.isConnecting = false;
    this.isStreaming = false;
  }

  private handleStreamingPaused(): void {
    this.isPaused = true;
  }

  private handleStreamingResumed(): void {
    this.isPaused = false;
  }

  private startDurationTimer(): void {
    this.streamingDuration = 0;
    this.durationInterval = window.setInterval(() => {
      this.streamingDuration++;
    }, 1000);
  }

  private stopDurationTimer(): void {
    if (this.durationInterval !== null) {
      clearInterval(this.durationInterval);
      this.durationInterval = null;
    }
  }

  private formatDuration(seconds: number): string {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }

  private handlePauseResume(): void {
    if (!this.streamRecorder) return;

    if (this.isPaused) {
      this.streamRecorder.resumeStreaming();
    } else {
      this.streamRecorder.pauseStreaming();
    }
  }

  private handleStop(): void {
    if (this.streamRecorder) {
      void this.streamRecorder.stopStreaming();
    }
  }

  private handleClose(): void {
    this.handleStop();
  }

  private handleEventWentLive(): void {
    this.isLive = true;
  }

  private handleEndEventClick(): void {
    this.showEndConfirmation = true;
  }

  private handleCancelEnd(): void {
    this.showEndConfirmation = false;
  }

  private async handleConfirmEnd(): Promise<void> {
    if (!this.streamId) return;

    try {
      this.isEnding = true;
      this.showEndConfirmation = false;
      const result = await rpc("end-live-event", { event_id: this.streamId });

      if (result.success) {
        NotificationService.success(t("editLiveEventModal.endEventSuccess") as string);
        this.handleStop();
        this.dispatchEvent(
          new CustomEvent("event-ended", {
            detail: { eventId: this.streamId },
            bubbles: true,
            composed: true,
          }),
        );
      } else {
        NotificationService.error(t("editLiveEventModal.endEventFailed") as string);
      }
    } catch (error) {
      console.error("Error ending live event:", error);
      NotificationService.error(t("editLiveEventModal.endEventFailed") as string);
    } finally {
      this.isEnding = false;
    }
  }

  private getBadgeStatus(): LiveBadgeStatus {
    if (this.isPaused) return "paused";
    if (this.isLive) return "live";
    return "preview";
  }

  private getBadgeLabel(): string {
    if (this.isPaused) return t("liveStreamModal.paused") as string;
    if (this.isLive) return t("liveStreamModal.live") as string;
    return t("liveStreamModal.preview") as string;
  }

  override render() {
    return html`
      <wow-modal prevent-close mobile-fullscreen @close=${this.handleClose}>
        <span slot="header">
          ${this.isStreaming
            ? html`
                <div class="streaming-header">
                  <live-badge status=${this.getBadgeStatus()} label=${this.getBadgeLabel()}></live-badge>
                  <span class="duration">${this.formatDuration(this.streamingDuration)}</span>
                  ${this.isLive
                    ? html`
                        <div class="header-controls">
                          <wow-button
                            variant="danger"
                            size="small"
                            square
                            @click=${this.handleEndEventClick}
                            ?disabled=${this.isEnding}
                            title="${t("editLiveEventModal.endEvent")}"
                          >
                            <ph-x size="18" weight="bold"></ph-x>
                          </wow-button>
                          <wow-button
                            variant="neutral"
                            size="small"
                            square
                            @click=${this.handlePauseResume}
                            title="${this.isPaused ? t("liveStreamModal.resume") : t("liveStreamModal.pause")}"
                          >
                            ${this.isPaused
                              ? html`<ph-play size="18" weight="fill"></ph-play>`
                              : html`<ph-pause size="18" weight="fill"></ph-pause>`}
                          </wow-button>
                          <wow-button
                            variant="primary"
                            size="small"
                            square
                            @click=${this.handleStop}
                            title="${t("liveStreamModal.stopStreaming")}"
                          >
                            <ph-stop size="18" weight="fill"></ph-stop>
                          </wow-button>
                        </div>
                      `
                    : nothing}
                </div>
              `
            : this.isConnecting
              ? t("liveStreamModal.connecting")
              : t("liveStreamModal.stream")}
        </span>

        <div class="streaming-content">
          ${this.errorMessage
            ? html`
                <div class="error-banner">
                  <ph-warning size="20"></ph-warning>
                  <span>${this.errorMessage}</span>
                </div>
              `
            : nothing}
          ${!this.isLive
            ? html`
                <div class="preview-info-banner">
                  <ph-info size="20" weight="fill"></ph-info>
                  <div class="preview-info-content">
                    <p>${t("editLiveEventModal.previewTitle")}</p>
                    <p>${t("editLiveEventModal.previewChecklist")}</p>
                    <ul>
                      <li>${t("editLiveEventModal.checkAudio")}</li>
                      <li>${t("editLiveEventModal.checkVideo")}</li>
                      <li>${t("editLiveEventModal.checkConnection")}</li>
                      <li>${t("editLiveEventModal.checkSettings")}</li>
                    </ul>
                    <p>${t("editLiveEventModal.previewAction")}</p>
                  </div>
                </div>
              `
            : nothing}
          ${this.isConnecting && !this.isStreaming
            ? html`
                <div class="connecting-state">
                  <div class="spinner"></div>
                  <span>${t("liveStreamModal.startingStream")}</span>
                </div>
              `
            : nothing}

          <div class="live-content">
            <div class="video-container">
              <stream-recorder
                .streamId=${this.streamId}
                ?enable-audio=${this.enableAudio}
                show-preview
                hide-controls
                @streaming-started=${this.handleStreamingStarted}
                @streaming-stopped=${this.handleStreamingStopped}
                @streaming-error=${this.handleStreamingError}
                @streaming-paused=${this.handleStreamingPaused}
                @streaming-resumed=${this.handleStreamingResumed}
              ></stream-recorder>
            </div>
            ${this.isLive && this.isStreaming
              ? html` <live-chat event-id=${this.streamId} event-status="live" is-authenticated></live-chat> `
              : nothing}
          </div>

          ${this.isStreaming && !this.isLive
            ? html`
                <div class="streaming-controls">
                  <go-live-button
                    .eventId=${this.streamId}
                    variant="danger"
                    size="medium"
                    ?full-width=${false}
                    .translations=${{
                      goLive: translate("editLiveEventModal.goLive"),
                      goingLive: translate("editLiveEventModal.goingLive") || "Going Live...",
                      goLiveSuccess: translate("editLiveEventModal.goLiveSuccess"),
                      goLiveFailed: translate("editLiveEventModal.goLiveFailed"),
                      goLiveHeader: translate("editLiveEventModal.goLiveHeader"),
                      goLiveConfirm: translate("editLiveEventModal.goLiveConfirm"),
                      goLiveButton: translate("editLiveEventModal.goLiveButton"),
                      goLiveCancel: translate("editLiveEventModal.goLiveCancel"),
                    }}
                    @event-went-live=${this.handleEventWentLive}
                  ></go-live-button>
                  <wow-button variant="neutral" size="medium" mobile-full-width @click=${this.handlePauseResume}>
                    ${this.isPaused
                      ? html`<ph-play size="18" weight="fill"></ph-play> ${t("liveStreamModal.resume")}`
                      : html`<ph-pause size="18" weight="fill"></ph-pause> ${t("liveStreamModal.pause")}`}
                  </wow-button>
                  <wow-button variant="primary" size="medium" mobile-full-width @click=${this.handleStop}>
                    <ph-stop size="18" weight="fill"></ph-stop>
                    ${t("liveStreamModal.stopStreaming")}
                  </wow-button>
                </div>
              `
            : nothing}
        </div>
      </wow-modal>
      ${this.showEndConfirmation
        ? html`
            <wow-confirmation header="${t("editLiveEventModal.endEventHeader")}">
              <div class="confirmation-content">
                <p>${t("editLiveEventModal.endEventConfirm")}</p>
                <div class="confirmation-buttons">
                  <wow-button variant="danger" size="medium" full-width @click=${this.handleConfirmEnd}>
                    ${t("editLiveEventModal.endEventButton")}
                  </wow-button>
                  <wow-button variant="neutral" size="medium" full-width @click=${this.handleCancelEnd}>
                    ${t("editLiveEventModal.endEventCancel")}
                  </wow-button>
                </div>
              </div>
            </wow-confirmation>
          `
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "live-stream-modal": LiveStreamModal;
  }
}
